import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  PlxsText,
  PlxsContentSplash,
  PlxsButton,
  PlxsCollapsePanel,
} from "@plexlogic/plexus-ui";
import { DrawResponse } from "../../services/api";
import apiService from "../../services/api";
import DrawCard from "./DrawCard";
import WizardSpinner from "../WizardSpinner";
import { PAGE_SIZE } from "../../config/pagination";
import "./DrawList.css";

export interface DrawListProps {
  title: string;
  statusFilter?: "draft" | "scheduled" | "completed" | "failed";
  onDeleteDraw: (drawId: string, status: string, event?: React.MouseEvent<HTMLElement>) => void;
  onViewActivity: (drawId: string) => void;
  onCreateNew: () => void;
  emptyMessage?: string;
  defaultExpanded?: boolean;
  // Trigger to refetch data (e.g., after deletion)
  refetchTrigger?: number;
}

export interface DrawListHandle {
  refetch: () => void;
  removeDrawOptimistically: (drawId: string) => void;
}

const DrawList = forwardRef<DrawListHandle, DrawListProps>(({
  title,
  statusFilter,
  onDeleteDraw,
  onViewActivity,
  onCreateNew,
  emptyMessage = "No draws found",
  defaultExpanded = true,
  refetchTrigger = 0,
}, ref) => {
  // Internal state for pagination-managed draws
  const [draws, setDraws] = useState<DrawResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  // Fetch draws when component mounts or filter changes
  const fetchDraws = useCallback(async () => {
    try {
      setLoading(true);

      const params: {
        status_filter?: "draft" | "scheduled" | "completed" | "failed";
        limit: number;
        offset: number;
      } = {
        limit: PAGE_SIZE,
        offset: 0,
      };

      if (statusFilter) {
        params.status_filter = statusFilter;
      }

      const response = await apiService.getDraws(params);
      setDraws(response.draws);
      setTotal(response.total);
      setHasMore(response.hasMore);
      setOffset(PAGE_SIZE); // Next offset for load more
    } catch (err) {
      // Error notification handled by interceptor
      setDraws([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchDraws();
  }, [fetchDraws, refetchTrigger]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    refetch: fetchDraws,
    removeDrawOptimistically: (drawId: string) => {
      setDraws((prevDraws) => prevDraws.filter((d) => d.draw_id !== drawId));
      setTotal((prevTotal) => Math.max(0, prevTotal - 1));
    }
  }), [fetchDraws]);

  // Handle Load More
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);

      const params: {
        status_filter?: "draft" | "scheduled" | "completed" | "failed";
        limit: number;
        offset: number;
      } = {
        limit: PAGE_SIZE,
        offset: offset,
      };

      if (statusFilter) {
        params.status_filter = statusFilter;
      }

      const response = await apiService.getDraws(params);

      // Merge new draws with existing ones, avoiding duplicates
      setDraws((prevDraws) => {
        const existingIds = new Set(prevDraws.map((d) => d.draw_id));
        const newDraws = response.draws.filter(
          (d) => !existingIds.has(d.draw_id)
        );
        return [...prevDraws, ...newDraws];
      });

      setHasMore(response.hasMore);
      setOffset((prev) => prev + PAGE_SIZE);
    } catch (err) {
      // Error notification handled by interceptor
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, offset, statusFilter]);

  // Determine spotlight icon and message based on title
  const getEmptyStateConfig = () => {
    const titleLower = title.toLowerCase();

    if (titleLower.includes("completed")) {
      return {
        icon: "check",
        title: "No completed draws",
      };
    } else if (titleLower.includes("scheduled")) {
      return {
        icon: "arrowClock",
        title: "No scheduled draws",
      };
    } else if (titleLower.includes("draft")) {
      return {
        icon: "edit",
        title: "No draft draws",
      };
    } else {
      return {
        icon: "grid",
        title: emptyMessage,
      };
    }
  };

  // Determine indicator tone based on category
  const getIndicatorTone = () => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes("completed")) return "positive";
    if (titleLower.includes("scheduled")) return "caution";
    if (titleLower.includes("draft")) return "neutral";
    return "neutral";
  };

  const emptyStateConfig = getEmptyStateConfig();

  // Show loading state
  if (loading) {
    return (
      <div className="draw-list">
        <PlxsCollapsePanel
          dataTestId={`draw-list-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}
          title={title}
          variant="tab"
          hasBorder={false}
          indicatorLabel="..."
          indicatorTone={getIndicatorTone() as any}
        >
          <div className="draw-list-loading">
            <WizardSpinner size={48} />
            <PlxsText>Loading draws...</PlxsText>
          </div>
        </PlxsCollapsePanel>
      </div>
    );
  }

  return (
    <div className="draw-list">
      {draws.length > 0 ? (
        <PlxsCollapsePanel
          dataTestId={`draw-list-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}
          title={title}
          variant="tab"
          hasBorder={false}
          indicatorLabel={total.toString()}
          indicatorTone={getIndicatorTone() as any}
        >
          <div className="draw-list-grid">
            {draws.map((draw) => (
              <DrawCard
                key={draw.draw_id}
                draw={draw}
                onDelete={(event) => onDeleteDraw(draw.draw_id, draw.status, event)}
                onViewActivity={() => onViewActivity(draw.draw_id)}
              />
            ))}
          </div>
          
          {/* Load More Button */}
          {hasMore && (
            <div className="draw-list-load-more">
              {loadingMore ? (
                <div className="draw-list-loading-more">
                  <WizardSpinner size={24} />
                  <PlxsText>Loading more draws...</PlxsText>
                </div>
              ) : (
                <PlxsButton
                  variant="secondary"
                  tone="brand"
                  label={`Load more (${draws.length} of ${total})`}
                  onClick={handleLoadMore}
                  dataTestId="load-more-button"
                  functionalIcon="arrowDown"
                />
              )}
            </div>
          )}
        </PlxsCollapsePanel>
      ) : (
        <div className="draw-list-empty">
          <PlxsContentSplash
            spotlightIcon={emptyStateConfig.icon as any}
            heading={emptyStateConfig.title}
          >
            <div className="draw-list-empty-content">
              <PlxsButton
                variant="primary"
                tone="positive"
                functionalIcon="add"
                label="Create new draw"
                onClick={onCreateNew}
                dataTestId="empty-state-create-draw"
              />
            </div>
          </PlxsContentSplash>
        </div>
      )}
    </div>
  );
});

// Memoize component with custom comparison to prevent unnecessary re-renders
// Only re-render if statusFilter, refetchTrigger, or callback references change
export default React.memo(DrawList, (prevProps, nextProps) => {
  return (
    prevProps.statusFilter === nextProps.statusFilter &&
    prevProps.refetchTrigger === nextProps.refetchTrigger &&
    prevProps.title === nextProps.title &&
    prevProps.emptyMessage === nextProps.emptyMessage &&
    prevProps.defaultExpanded === nextProps.defaultExpanded &&
    prevProps.onDeleteDraw === nextProps.onDeleteDraw &&
    prevProps.onViewActivity === nextProps.onViewActivity &&
    prevProps.onCreateNew === nextProps.onCreateNew
  );
});
